import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import { AttributesList } from "@/components/ui/chat/attribute-filters/AttributesList";
import { ChatAttributeFilter } from "@/components/ui/chat/chatFilterUtils";
import { VocSearchTagFilter } from "@/components/ui/VocSearchTagFilter";
import { useAttributes } from "@/hooks/useAttributes";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { useChatStore } from "@/stores/ui/useChatStore";

export const AdvancedFilterTab: React.FC = () => {
  const { t } = useTranslation("voiceek_ui");
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const { attributes, isLoading } = useAttributes(sessionCurrentWorkspaceId, true);

  const { attributeFilters, tagIds } = useChatStore((s) => {
    return s.getCurrentConversationState();
  });
  const setCurrentConversationState = useChatStore((s) => s.setCurrentConversationState);

  // フィルタが設定されている属性を初期状態で展開する
  const initialExpandedAttributes = useMemo(() => {
    return new Set(Object.keys(attributeFilters));
  }, [attributeFilters]);

  const [expandedAttributes, setExpandedAttributes] = useState<Set<string>>(initialExpandedAttributes);

  const toggleAttribute = (attributeName: string) => {
    setExpandedAttributes((prev) => {
      const next = new Set(prev);
      if (next.has(attributeName)) {
        next.delete(attributeName);
      } else {
        next.add(attributeName);
      }
      return next;
    });
  };

  const toggleTag = (tagId: string) => {
    const currentTagIds = tagIds || [];
    if (currentTagIds.includes(tagId)) {
      setCurrentConversationState({ tagIds: currentTagIds.filter((id) => id !== tagId) });
    } else {
      setCurrentConversationState({ tagIds: [...currentTagIds, tagId] });
    }
  };

  const handleFilterChange = (attributeName: string, filter: ChatAttributeFilter | null) => {
    if (filter) {
      setCurrentConversationState({
        attributeFilters: { ...attributeFilters, [attributeName]: filter },
      });
    } else {
      const { [attributeName]: _, ...restAttr } = attributeFilters;
      setCurrentConversationState({ attributeFilters: restAttr });
    }
  };

  return (
    <ScrollArea className="h-full">
      <div className="flex gap-6 pr-6 min-h-10">
        <div className="flex items-start pt-[10px]  w-[60px]">
          <p className="text-[14px] font-semibold text-[#333333]">
            {t("chat.filter.attributes")}
          </p>
        </div>

        <div className="flex items-stretch self-stretch">
          <div className="w-[2px] bg-[#d9d9d9]" />
        </div>

        <AttributesList
          attributes={attributes}
          isLoading={isLoading}
          expandedAttributes={expandedAttributes}
          attributeFilters={attributeFilters}
          onToggleAttribute={toggleAttribute}
          onFilterChange={handleFilterChange}
        />
      </div>

      <div className="mt-8">
        <VocSearchTagFilter
          tagIds={tagIds || []}
          onToggleTagId={toggleTag}
        />
      </div>

      <ScrollBar orientation="vertical" />
    </ScrollArea>
  );
};
